import { useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Sparkles } from 'lucide-react';

/* ---------- Personal Info + Summary ---------- */
export function PersonalInfoSection({ personalInfo, setPersonalInfo, professionalSummary, setProfessionalSummary, onOpenAI }) {
  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Personal Information</h3>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { key: 'name', label: 'Full Name', testId: 'personal-name-input' },
            { key: 'email', label: 'Email', testId: 'personal-email-input', type: 'email' },
            { key: 'phone', label: 'Phone', testId: 'personal-phone-input' },
            { key: 'linkedin', label: 'LinkedIn', testId: 'personal-linkedin-input' },
          ].map(({ key, label, testId, type }) => (
            <div key={key}>
              <Label>{label}</Label>
              <Input
                data-testid={testId}
                type={type || 'text'}
                value={personalInfo[key]}
                onChange={(e) => setPersonalInfo({ ...personalInfo, [key]: e.target.value })}
                className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-transparent focus:border-[#FB923C] focus:ring-0 px-0 py-3"
              />
            </div>
          ))}
          <div className="md:col-span-2">
            <Label>Location</Label>
            <Input
              data-testid="personal-location-input"
              value={personalInfo.location}
              onChange={(e) => setPersonalInfo({ ...personalInfo, location: e.target.value })}
              className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-transparent focus:border-[#FB923C] focus:ring-0 px-0 py-3"
            />
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Professional Summary</h3>
          <Button
            data-testid="ai-summary-btn"
            onClick={onOpenAI}
            className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-4 py-2 text-sm"
          >
            <Sparkles className="w-4 h-4 mr-1" />
            AI Generate
          </Button>
        </div>
        <Textarea
          data-testid="professional-summary-input"
          value={professionalSummary}
          onChange={(e) => setProfessionalSummary(e.target.value)}
          rows={4}
          className="rounded-none border border-slate-200 dark:border-slate-600 focus:border-[#FB923C] focus:ring-0 dark:bg-slate-800"
          placeholder="Brief overview of your professional background and goals..."
        />
      </div>
    </div>
  );
}

/* ---------- Work Experience ---------- */
function WorkEntry({ work, index, onUpdate, onDelete, onOpenAI }) {
  const updateField = useCallback((field, value) => {
    onUpdate(index, { ...work, [field]: value });
  }, [index, work, onUpdate]);

  const updateBullet = useCallback((bIdx, value) => {
    const bullets = [...(work.bullets || [])];
    bullets[bIdx] = value;
    onUpdate(index, { ...work, bullets });
  }, [index, work, onUpdate]);

  const removeBullet = useCallback((bIdx) => {
    const bullets = (work.bullets || []).filter((_, i) => i !== bIdx);
    onUpdate(index, { ...work, bullets });
  }, [index, work, onUpdate]);

  const addBullet = useCallback(() => {
    const bullets = [...(work.bullets || []), ''];
    onUpdate(index, { ...work, bullets });
  }, [index, work, onUpdate]);

  return (
    <div data-testid={`work-entry-${index}`} className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-6 space-y-4">
      <div className="flex justify-between">
        <h4 className="font-bold text-slate-900 dark:text-white">Position {index + 1}</h4>
        <Button
          data-testid={`delete-work-btn-${index}`}
          onClick={() => onDelete(index)}
          className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-none px-3 py-1 text-sm"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {[
          { field: 'title', label: 'Job Title', testId: `work-title-${index}` },
          { field: 'company', label: 'Company', testId: `work-company-${index}` },
          { field: 'startDate', label: 'Start Date', testId: `work-start-${index}`, placeholder: 'Jan 2020' },
          { field: 'endDate', label: 'End Date', testId: `work-end-${index}`, placeholder: 'Present' },
        ].map(({ field, label, testId, placeholder }) => (
          <div key={field}>
            <Label>{label}</Label>
            <Input
              data-testid={testId}
              value={work[field] || ''}
              onChange={(e) => updateField(field, e.target.value)}
              placeholder={placeholder}
              className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-[#FB923C] focus:ring-0 px-0 py-2"
            />
          </div>
        ))}
      </div>
      <div>
        <div className="flex justify-between items-center mb-2">
          <Label>Responsibilities</Label>
          <Button
            data-testid={`ai-bullet-btn-${index}`}
            onClick={() => onOpenAI(index)}
            className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-3 py-1 text-xs"
          >
            <Sparkles className="w-3 h-3 mr-1" />
            AI
          </Button>
        </div>
        {(work.bullets || []).map((bullet, bIdx) => (
          <div key={`bullet-${index}-${bIdx}`} className="flex gap-2 mb-2">
            <Input
              data-testid={`work-bullet-${index}-${bIdx}`}
              value={bullet}
              onChange={(e) => updateBullet(bIdx, e.target.value)}
              className="flex-1 rounded-none border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-[#FB923C] focus:ring-0 px-3 py-2"
            />
            <Button
              onClick={() => removeBullet(bIdx)}
              className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-none px-3 py-2"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        <Button
          onClick={addBullet}
          className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 rounded-none px-4 py-2 text-sm w-full mt-2"
        >
          + Add Bullet Point
        </Button>
      </div>
    </div>
  );
}

export function WorkExperienceSection({ workExperience, setWorkExperience, onOpenAI }) {
  const handleUpdate = useCallback((index, updated) => {
    setWorkExperience((prev) => prev.map((item, i) => (i === index ? updated : item)));
  }, [setWorkExperience]);

  const handleDelete = useCallback((index) => {
    setWorkExperience((prev) => prev.filter((_, i) => i !== index));
  }, [setWorkExperience]);

  const handleAdd = useCallback(() => {
    setWorkExperience((prev) => [...prev, { _uid: crypto.randomUUID(), title: '', company: '', location: '', startDate: '', endDate: '', bullets: [] }]);
  }, [setWorkExperience]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Work Experience</h3>
        <Button
          data-testid="add-work-btn"
          onClick={handleAdd}
          className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-4 py-2"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Position
        </Button>
      </div>
      {workExperience.map((work, idx) => (
        <WorkEntry
          key={work._uid || `work-${idx}`}
          work={work}
          index={idx}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onOpenAI={onOpenAI}
        />
      ))}
    </div>
  );
}

/* ---------- Education ---------- */
function EducationEntry({ edu, index, onUpdate, onDelete }) {
  const updateField = useCallback((field, value) => {
    onUpdate(index, { ...edu, [field]: value });
  }, [index, edu, onUpdate]);

  return (
    <div data-testid={`education-entry-${index}`} className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-6 space-y-4">
      <div className="flex justify-between">
        <h4 className="font-bold text-slate-900 dark:text-white">Education {index + 1}</h4>
        <Button
          data-testid={`delete-education-btn-${index}`}
          onClick={() => onDelete(index)}
          className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-none px-3 py-1 text-sm"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {[
          { field: 'degree', label: 'Degree', testId: `education-degree-${index}` },
          { field: 'institution', label: 'Institution', testId: `education-institution-${index}` },
          { field: 'year', label: 'Year', testId: `education-year-${index}`, placeholder: '2020' },
          { field: 'gpa', label: 'GPA (Optional)', testId: `education-gpa-${index}`, placeholder: '3.8/4.0' },
        ].map(({ field, label, testId, placeholder }) => (
          <div key={field}>
            <Label>{label}</Label>
            <Input
              data-testid={testId}
              value={edu[field] || ''}
              onChange={(e) => updateField(field, e.target.value)}
              placeholder={placeholder}
              className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-[#FB923C] focus:ring-0 px-0 py-2"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function EducationSection({ education, setEducation }) {
  const handleUpdate = useCallback((index, updated) => {
    setEducation((prev) => prev.map((item, i) => (i === index ? updated : item)));
  }, [setEducation]);

  const handleDelete = useCallback((index) => {
    setEducation((prev) => prev.filter((_, i) => i !== index));
  }, [setEducation]);

  const handleAdd = useCallback(() => {
    setEducation((prev) => [...prev, { _uid: crypto.randomUUID(), degree: '', institution: '', year: '', gpa: '' }]);
  }, [setEducation]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Education</h3>
        <Button
          data-testid="add-education-btn"
          onClick={handleAdd}
          className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-4 py-2"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Education
        </Button>
      </div>
      {education.map((edu, idx) => (
        <EducationEntry key={edu._uid || `edu-${idx}`} edu={edu} index={idx} onUpdate={handleUpdate} onDelete={handleDelete} />
      ))}
    </div>
  );
}

/* ---------- Extras (Skills, Projects, Certifications) ---------- */
export function ExtrasSection({ skills, setSkills, projects, setProjects, certifications, setCertifications }) {
  const addProject = useCallback(() => {
    setProjects((prev) => [...prev, { _uid: crypto.randomUUID(), title: '', description: '', techStack: '', link: '' }]);
  }, [setProjects]);

  const updateProject = useCallback((index, updated) => {
    setProjects((prev) => prev.map((item, i) => (i === index ? updated : item)));
  }, [setProjects]);

  const deleteProject = useCallback((index) => {
    setProjects((prev) => prev.filter((_, i) => i !== index));
  }, [setProjects]);

  const addCert = useCallback(() => {
    setCertifications((prev) => [...prev, { _uid: crypto.randomUUID(), name: '', issuer: '', year: '' }]);
  }, [setCertifications]);

  const updateCert = useCallback((index, updated) => {
    setCertifications((prev) => prev.map((item, i) => (i === index ? updated : item)));
  }, [setCertifications]);

  const deleteCert = useCallback((index) => {
    setCertifications((prev) => prev.filter((_, i) => i !== index));
  }, [setCertifications]);

  return (
    <div className="space-y-8">
      {/* Skills */}
      <div>
        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Skills</h3>
        {['technical', 'soft', 'languages'].map((category) => (
          <div key={category} className="mb-4">
            <Label className="capitalize">{category} Skills</Label>
            <Input
              data-testid={`skills-${category}-input`}
              value={skills[category].join(', ')}
              onChange={(e) => setSkills({ ...skills, [category]: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              placeholder="Enter skills separated by commas"
              className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-transparent focus:border-[#FB923C] focus:ring-0 px-0 py-3"
            />
          </div>
        ))}
      </div>

      {/* Projects */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Projects</h3>
          <Button data-testid="add-project-btn" onClick={addProject} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-4 py-2 text-sm">
            <Plus className="w-4 h-4 mr-1" /> Add Project
          </Button>
        </div>
        {projects.map((project, idx) => (
          <div key={project._uid || `proj-${idx}`} data-testid={`project-entry-${idx}`} className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-6 space-y-4 mb-4">
            <div className="flex justify-between">
              <h4 className="font-bold text-slate-900 dark:text-white">Project {idx + 1}</h4>
              <Button onClick={() => deleteProject(idx)} className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-none px-3 py-1 text-sm">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-3">
              <Input data-testid={`project-title-${idx}`} value={project.title} onChange={(e) => updateProject(idx, { ...project, title: e.target.value })} placeholder="Project Title" className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-[#FB923C] focus:ring-0 px-0 py-2" />
              <Textarea value={project.description} onChange={(e) => updateProject(idx, { ...project, description: e.target.value })} placeholder="Description" rows={2} className="rounded-none border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-[#FB923C] focus:ring-0" />
              <Input value={project.techStack} onChange={(e) => updateProject(idx, { ...project, techStack: e.target.value })} placeholder="Tech Stack (React, Node.js, etc.)" className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-[#FB923C] focus:ring-0 px-0 py-2" />
              <Input value={project.link} onChange={(e) => updateProject(idx, { ...project, link: e.target.value })} placeholder="Project Link (optional)" className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-[#FB923C] focus:ring-0 px-0 py-2" />
            </div>
          </div>
        ))}
      </div>

      {/* Certifications */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Certifications</h3>
          <Button data-testid="add-certification-btn" onClick={addCert} className="bg-[#FB923C] hover:bg-orange-300 text-slate-900 font-bold rounded-none px-4 py-2 text-sm">
            <Plus className="w-4 h-4 mr-1" /> Add Certification
          </Button>
        </div>
        {certifications.map((cert, idx) => (
          <div key={cert._uid || `cert-${idx}`} data-testid={`certification-entry-${idx}`} className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-6 space-y-4 mb-4">
            <div className="flex justify-between">
              <h4 className="font-bold text-slate-900 dark:text-white">Certification {idx + 1}</h4>
              <Button onClick={() => deleteCert(idx)} className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-none px-3 py-1 text-sm">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Input data-testid={`certification-name-${idx}`} value={cert.name} onChange={(e) => updateCert(idx, { ...cert, name: e.target.value })} placeholder="Certification Name" className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-[#FB923C] focus:ring-0 px-0 py-2" />
              <Input value={cert.issuer} onChange={(e) => updateCert(idx, { ...cert, issuer: e.target.value })} placeholder="Issuer" className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-[#FB923C] focus:ring-0 px-0 py-2" />
              <Input value={cert.year} onChange={(e) => updateCert(idx, { ...cert, year: e.target.value })} placeholder="Year" className="rounded-none border-b-2 border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 focus:border-[#FB923C] focus:ring-0 px-0 py-2" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
